import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { Page } from 'components/Page';
import { FieldRepeatableListItem } from 'screens/AssetScreen/Field/FieldRepeatableListItem';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, SET_GENERIC } from 'store/app/actions';
import {
  selectAssetFieldRepeatableInstanceIdsForGroup,
  selectAssetFieldRepeatableInstanceIdsForGroupFilteredAndSorted,
  selectAssetOrganisationId,
} from 'store/assets/selectors';
import { selectOrganisationFieldGroup } from 'store/organisations/selectors';
import {
  doAssetFindFieldInstanceByPin,
  doAssetFindFieldInstanceByQR,
  doAssetOpenOverview,
} from 'store/actions';
import { pluralise } from 'lib/pluralise';
import { GENERICKEY_FIELDREPEATABLELIST } from 'lib/riskbase';
import { selectGeneric } from 'store/app/selectors';
import { ScreenSize } from 'lib/constants';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import { ListRenderItem } from '@shopify/flash-list';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';
import { ButtonGroup } from 'components/ButtonGroup';
import { List } from 'components/List';
import { PageEmpty } from 'components/PageEmpty';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSETFIELDREPEATABLE],
  Screens.ASSETFIELDREPEATABLE_LIST
>;

export const FieldRepeatableListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const { assetId, groupId } = route.params;

  const organisationId = useSelector((state) =>
    selectAssetOrganisationId(state, assetId),
  );

  const organisationUid = useSelector((state) =>
    selectUserOrganisationUuidToUid(state, organisationId),
  );
  const userFeatures = useSelector((state) =>
    selectUserOrganisationFeatures(state, organisationUid),
  );

  const instanceIds = useSelector((state) =>
    selectAssetFieldRepeatableInstanceIdsForGroup(state, assetId, groupId),
  );

  const instanceIdsFilteredAndSorted = useSelector((state) =>
    selectAssetFieldRepeatableInstanceIdsForGroupFilteredAndSorted(
      state,
      assetId,
      groupId,
    ),
  );

  const group = useSelector((state) =>
    selectOrganisationFieldGroup(state, organisationId, groupId),
  );

  const genericKeySearchString = GENERICKEY_FIELDREPEATABLELIST({
    groupId,
    where: 'onasset',
    type: 'searchString',
  });

  const searchValue =
    (useSelector((state) =>
      selectGeneric(state, genericKeySearchString),
    ) as string) || '';

  const renderItem: ListRenderItem<string> = React.useCallback(
    ({ item }) => (
      <FieldRepeatableListItem
        key={item}
        id={item}
        group={group!}
        assetId={assetId}
        isEditable={userFeatures.editassetfields}
      />
    ),
    [assetId, group, userFeatures.editassetfields],
  );

  const handleSearch = React.useCallback(
    (search: string) => {
      dispatch(
        SET_GENERIC({
          key: genericKeySearchString,
          value: search,
          temp: true,
        }),
      );
    },
    [dispatch, genericKeySearchString],
  );

  React.useEffect(() => {
    if (!group) {
      if (screenSize === ScreenSize.large) {
        dispatch(doAssetOpenOverview({ assetId }));
      } else {
        navigation.goBack();
      }
    }
  }, [group, dispatch, screenSize, assetId, navigation]);

  if (!group) {
    return null;
  }

  const { pinFieldId, qrFieldId } = group.calculatedInfo;

  return (
    <Page
      header={
        <>
          <PageHeader
            title={pluralise(
              instanceIds.length,
              `1 ${group.calculatedInfo.singularName}`,
              `{x} ${group.name}`,
              group.name,
            )}
            onPressBackButton={
              screenSize === 'small' ? navigation.goBack : undefined
            }
            iconButtonRight={
              userFeatures.editassetfields
                ? {
                    icon: ICONS.PlusLight,
                    onPress: () =>
                      dispatch(
                        NAVIGATE_MODAL({
                          screen: ModalScreens.NEWREPEATABLEINSTANCE,
                          params: {
                            assetId,
                            groupId,
                          },
                        }),
                      ),
                  }
                : undefined
            }
          />
          {!!instanceIds.length && (
            <PageToolbar
              onSearch={handleSearch}
              searchPlaceholder={`Search ${group.name}`}
              searchValue={searchValue}
            >
              {!!instanceIds.length && !!qrFieldId && !!pinFieldId && (
                <ButtonGroup>
                  {!!qrFieldId && (
                    <IconButton
                      icon={ICONS.QRCodeLight}
                      onPress={() =>
                        dispatch(
                          doAssetFindFieldInstanceByQR({ assetId, groupId }),
                        )
                      }
                    />
                  )}
                  {!!pinFieldId && (
                    <IconButton
                      icon={ICONS.LocationPinSolidStroke}
                      onPress={() =>
                        dispatch(
                          doAssetFindFieldInstanceByPin({ assetId, groupId }),
                        )
                      }
                    />
                  )}
                </ButtonGroup>
              )}
              <ButtonGroup>
                <IconButton
                  icon={ICONS.SlidersUpLight}
                  onPress={() =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.FIELDINSTANCESOPTIONS,
                        params: { groupId, organisationId },
                      }),
                    )
                  }
                />
              </ButtonGroup>
            </PageToolbar>
          )}
        </>
      }
    >
      {instanceIdsFilteredAndSorted.length ? (
        <List
          keyExtractor={(id) => id}
          data={instanceIdsFilteredAndSorted}
          renderItem={renderItem}
        />
      ) : (
        <PageEmpty
          text={
            instanceIds.length || !userFeatures.editassetfields
              ? 'No Results Found'
              : ''
          }
          subtext={instanceIds.length ? 'Try a different search' : ''}
          button={
            instanceIds.length || !userFeatures.editassetfields
              ? undefined
              : {
                  text: `Add ${group.calculatedInfo.singularName}`,
                  onPress: () =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.NEWREPEATABLEINSTANCE,
                        params: {
                          assetId,
                          groupId,
                        },
                      }),
                    ),
                }
          }
        />
      )}
    </Page>
  );
};
